// SettingsPanel.jsx

import React, { useState, useEffect } from "react";
import { useVideo } from "../../context/VideoContext";
import { useSubtitleSettings } from "../../context/SubtitleSettingsContext";
import { safeGetItem, safeSetItem } from "../../utils/safeStorage";
import "./SettingsPanel.css";

const SettingsPanel = () => {
  const { setShowSettings } = useVideo();
  const { settings, updateSubtitleSetting } = useSubtitleSettings();

  // state برای تنظیمات پخش تمام صفحه
  const [autoFullscreen, setAutoFullscreen] = useState(() => {
    const saved = safeGetItem("autoFullscreenOnVideoSelect");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // state برای نمایش پنجره جستجوی زیرنویس
  const [showSubtitleSearchPopup, setShowSubtitleSearchPopup] = useState(() => {
    const saved = safeGetItem("subtitleNotFoundSetting");
    return saved !== "dont-show";
  });

  // state برای نمایش زیرنویس‌های پیشنهادی دیگر کاربران
  const [showOtherUsersSubtitles, setShowOtherUsersSubtitles] = useState(() => {
    const saved = safeGetItem("showOtherUsersSubtitles");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // state برای کنترل آکاردئون‌ها
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    subtitle1: false,
    subtitle2: false,
  });

  // ذخیره تنظیمات پخش تمام صفحه
  useEffect(() => {
    safeSetItem(
      "autoFullscreenOnVideoSelect",
      JSON.stringify(autoFullscreen)
    );
  }, [autoFullscreen]);

  // ذخیره تنظیمات نمایش پنجره جستجو
  useEffect(() => {
    safeSetItem(
      "subtitleNotFoundSetting",
      showSubtitleSearchPopup ? "show" : "dont-show"
    );
  }, [showSubtitleSearchPopup]);

  // ذخیره تنظیمات نمایش زیرنویس‌های پیشنهادی دیگران
  useEffect(() => {
    safeSetItem(
      "showOtherUsersSubtitles",
      JSON.stringify(showOtherUsersSubtitles)
    );
  }, [showOtherUsersSubtitles]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const translateServices = [
    { value: "bing", label: "Bing", icon: "/icons/bing.png" },
    { value: "google", label: "Google", icon: "/icons/google.png" },
    { value: "glosbe", label: "Glosbe", icon: "/icons/glosbe.png" },
  ];

  return (
    <div
      className="settings-overlay"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-header">
          <div className="header-content">
            <i className="fas fa-cog header-icon"></i>
            <h3>تنظیمات</h3>
          </div>
          <button
            className="settings-close"
            onClick={() => setShowSettings(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="settings-content">
          {/* ========== بخش تنظیمات عمومی ========== */}
          <div className="setting-section">
            <div
              className="section-header"
              onClick={() => toggleSection("general")}
            >
              <div className="section-title">
                <i className="fas fa-sliders-h section-icon"></i>
                <h4>تنظیمات عمومی</h4>
              </div>
              <i
                className={`fas fa-chevron-down toggle-icon ${
                  expandedSections.general ? "expanded" : ""
                }`}
              ></i>
            </div>

            <div
              className={`section-content ${
                expandedSections.general ? "expanded" : ""
              }`}
            >
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">پخش تمام صفحه خودکار</span>
                  <span className="setting-desc">
                    هنگام انتخاب فیلم، به صورت خودکار تمام صفحه شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={autoFullscreen}
                    onChange={(e) => setAutoFullscreen(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">
                    نمایش پنجره جستجوی زیرنویس
                  </span>
                  <span className="setting-desc">
                    هنگام شروع پخش فیلم، در صورت نداشتن زیرنویس این پنجره نمایش
                    داده شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showSubtitleSearchPopup}
                    onChange={(e) =>
                      setShowSubtitleSearchPopup(e.target.checked)
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">
                    نمایش زیرنویسهای پیشنهادی دیگران
                  </span>
                  <span className="setting-desc">
                    در صورت وجود زیرنویس پیشنهادی از طرف سایر کاربران، به صورت
                    لیست نمایش داده شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showOtherUsersSubtitles}
                    onChange={(e) =>
                      setShowOtherUsersSubtitles(e.target.checked)
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* ========== بخش دکمه‌های زیرنویس اول ========== */}
          <div className="setting-section">
            <div
              className="section-header"
              onClick={() => toggleSection("subtitle1")}
            >
              <div className="section-title">
                <i className="fas fa-comment-dots section-icon"></i>
                <h4>دکمه‌های زیرنویس اول (انگلیسی/آلمانی/...)</h4>
              </div>
              <i
                className={`fas fa-chevron-down toggle-icon ${
                  expandedSections.subtitle1 ? "expanded" : ""
                }`}
              ></i>
            </div>

            <div
              className={`section-content ${
                expandedSections.subtitle1 ? "expanded" : ""
              }`}
            >
              {/* 👈 تنظیم جدید: نمایش دکمه چشم */}
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">
                    <i
                      className="fas fa-eye"
                      style={{ marginRight: 6, color: "#ffa726" }}
                    ></i>
                    نمایش دکمه چشم (پنهان/نمایش زیرنویس)
                  </span>
                  <span className="setting-desc">
                    دکمه‌ای کنار زیرنویس برای پنهان یا نمایش سریع زیرنویس
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.subtitle1.showEyeButton}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        1,
                        "showEyeButton",
                        e.target.checked
                      )
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">نمایش دکمه ترجمه</span>
                  <span className="setting-desc">
                    دکمه ترجمه کلمات در کنار زیرنویس نمایش داده شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.subtitle1.showTranslateButton}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        1,
                        "showTranslateButton",
                        e.target.checked
                      )
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {settings.subtitle1.showTranslateButton && (
                <div className="setting-item nested">
                  <div className="setting-info">
                    <span className="setting-label">سرویس ترجمه</span>
                  </div>
                  <div className="radio-group">
                    {translateServices.map((service) => (
                      <label key={service.value} className="radio-label">
                        <input
                          type="radio"
                          name="sub1-translate-service"
                          value={service.value}
                          checked={
                            settings.subtitle1.translateService ===
                            service.value
                          }
                          onChange={(e) =>
                            updateSubtitleSetting(
                              1,
                              "translateService",
                              e.target.value
                            )
                          }
                        />
                        <img src={service.icon} alt={service.label} />
                        <span>{service.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">نمایش دکمه ذخیره</span>
                  <span className="setting-desc">
                    دکمه ذخیره کلمات در کنار زیرنویس نمایش داده شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.subtitle1.showSaveButton}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        1,
                        "showSaveButton",
                        e.target.checked
                      )
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* ========== بخش دکمه‌های زیرنویس دوم ========== */}
          <div className="setting-section">
            <div
              className="section-header"
              onClick={() => toggleSection("subtitle2")}
            >
              <div className="section-title">
                <i className="fas fa-comment-dots section-icon"></i>
                <h4>دکمه‌های زیرنویس دوم (فارسی)</h4>
              </div>
              <i
                className={`fas fa-chevron-down toggle-icon ${
                  expandedSections.subtitle2 ? "expanded" : ""
                }`}
              ></i>
            </div>

            <div
              className={`section-content ${
                expandedSections.subtitle2 ? "expanded" : ""
              }`}
            >
              {/* 👈 تنظیم جدید: نمایش دکمه چشم */}
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">
                    <i
                      className="fas fa-eye"
                      style={{ marginRight: 6, color: "#ffa726" }}
                    ></i>
                    نمایش دکمه چشم (پنهان/نمایش زیرنویس)
                  </span>
                  <span className="setting-desc">
                    دکمه‌ای کنار زیرنویس برای پنهان یا نمایش سریع زیرنویس
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.subtitle2.showEyeButton}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        2,
                        "showEyeButton",
                        e.target.checked
                      )
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">نمایش دکمه ترجمه</span>
                  <span className="setting-desc">
                    دکمه ترجمه کلمات در کنار زیرنویس نمایش داده شود
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.subtitle2.showTranslateButton}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        2,
                        "showTranslateButton",
                        e.target.checked
                      )
                    }
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {settings.subtitle2.showTranslateButton && (
                <div className="setting-item nested">
                  <div className="setting-info">
                    <span className="setting-label">سرویس ترجمه</span>
                  </div>
                  <div className="radio-group">
                    {translateServices.map((service) => (
                      <label key={service.value} className="radio-label">
                        <input
                          type="radio"
                          name="sub2-translate-service"
                          value={service.value}
                          checked={
                            settings.subtitle2.translateService ===
                            service.value
                          }
                          onChange={(e) =>
                            updateSubtitleSetting(
                              2,
                              "translateService",
                              e.target.value
                            )
                          }
                        />
                        <img src={service.icon} alt={service.label} />
                        <span>{service.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* دکمه تایید */}
          <button className="done-btn" onClick={() => setShowSettings(false)}>
            <i className="fas fa-check"></i>
            تایید
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
